import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import clsx from 'clsx';
import { Input } from '../Input/Input';
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-icon.svg';
import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow-left.svg';

import { KpiItem, ChatMessage } from '../../types';
import { Typography } from '../Typography/Typography';
import { Button } from '../Button/Button';
import styles from './KpiDetailModal.module.scss';

type Tab = 'chat' | 'history';

interface Props {
  isOpen: boolean;
  isEditing: boolean;
  onClose: () => void;
  kpi: KpiItem | null;
  onDataChange?: (kpi: KpiItem) => void;
  isDiscussion?: boolean;
}

type EditingCell = {
  year: number;
  field: 'fact6months' | 'fact9months' | 'fact';
};

export function KpiDetailModal({
  isOpen,
  isEditing,
  onClose,
  kpi,
  onDataChange,
  isDiscussion
}: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('chat');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [kpiData, setKpiData] = useState<KpiItem | null>(kpi);
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (kpi) {
      setKpiData(kpi);
      if (kpi.chat) setMessages(kpi.chat);
    }
  }, [kpi]);

  useEffect(() => {
    if (activeTab === 'chat') {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  useEffect(() => {
    if (!isOpen) return;
    function handleKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const startEditing = (
    year: number,
    field: 'fact6months' | 'fact9months' | 'fact',
    currentValue: number | null
  ) => {
    setEditingCell({ year, field });
    setEditValue(currentValue?.toString() ?? '');
  };

  const saveEdit = useCallback(() => {
    if (!editingCell || !kpiData) return;

    const { year, field } = editingCell;
    const numValue = editValue.trim() === '' ? null : Number(editValue);

    const updatedKpi = {
      ...kpiData,
      years: kpiData.years.map((y) => {
        if (y.year !== year) return y;
        return { ...y, [field]: numValue };
      })
    };

    setKpiData(updatedKpi);
    onDataChange?.(updatedKpi);
    setEditingCell(null);
    setEditValue('');
  }, [editingCell, editValue, kpiData, onDataChange]);

  function handleSend() {
    const text = message.trim();
    if (!text) return;
    setMessages((prev) => [
      ...prev,
      {
        id: String(Date.now()),
        author: 'Вы',
        text,
        time: new Date().toLocaleString('ru-RU', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        }),
        isOwn: true
      }
    ]);
    setMessage('');
  }

  function handleKeydown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!isOpen || !kpiData) return null;

  const prevAuthor = { current: '' };

  return createPortal(
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.drawer} role="dialog" aria-modal="true">
        <button className={styles.back} onClick={onClose}>
          <ArrowIcon />
          Назад
        </button>

        <div className={styles.header}>
          <Typography variant={'h2'} className={styles.title}>
            {kpiData.name}
          </Typography>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Закрыть"
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.meta}>
          <span className={styles.metaItem}>
            <span className={styles.metaLabel}>Ед. изм.</span>
            {kpiData.unit}
          </span>
          <div className={styles.metaBlock}>
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>Вес</span>
              {kpiData.weight}
            </span>
            <a
              href={kpiData.methodologyUrl}
              className={styles.methodLink}
              target="_blank"
              rel="noreferrer"
            >
              Методика&nbsp; <InfoIcon className={styles.infoIcon} />
            </a>
          </div>
        </div>

        <div className={styles.table}>
          <KpiMiniTable
            kpi={kpiData}
            isEditing={isEditing}
            editingCell={editingCell}
            editValue={editValue}
            onStartEditing={startEditing}
            onEditValueChange={setEditValue}
            onSaveEdit={saveEdit}
          />
        </div>

        <div className={styles.dept}>
          <span className={styles.deptLabel}>Оценивающее подразделение</span>
          <span className={styles.deptValue}>{kpiData.department}</span>
        </div>

        {isDiscussion && (
          <div className={styles.discusAction}>
            <Button
              variant={'primary'}
              onClick={() => alert('Согласиться с расчетом')}
            >
              Согласиться с расчетом
            </Button>
            <Button
              variant={'secondary'}
              onClick={() => alert('Оспорить у руководителя')}
            >
              Оспорить у руководителя
            </Button>
          </div>
        )}
        {isEditing && (
          <div className={styles.saveBtn}>
            <Button variant={'primary'} onClick={() => alert('Сохранить')}>
              Сохранить
            </Button>
          </div>
        )}

        <div className={styles.tabs}>
          <button
            className={clsx(styles.tab, {
              [styles.tabActive]: activeTab === 'chat'
            })}
            onClick={() => setActiveTab('chat')}
          >
            Обсуждение КПЭ
          </button>
          <button
            className={clsx(styles.tab, {
              [styles.tabActive]: activeTab === 'history'
            })}
            onClick={() => setActiveTab('history')}
          >
            История изменений
            {kpiData.hasNewHistory && <span className={styles.dot} />}
          </button>
        </div>

        {activeTab === 'chat' && (
          <div className={styles.chatWrapper}>
            <div className={styles.chatMessages}>
              {messages.map((msg) => {
                const showAuthor =
                  msg.author !== prevAuthor.current && !msg.isOwn;
                prevAuthor.current = msg.author;
                return (
                  <div
                    key={msg.id}
                    className={clsx(styles.msgGroup, {
                      [styles.msgGroupOwn]: msg.isOwn
                    })}
                  >
                    {showAuthor && (
                      <span className={styles.msgAuthor}>{msg.author}</span>
                    )}
                    {msg.isOwn && (
                      <span
                        className={clsx(styles.msgAuthor, styles.msgAuthorOwn)}
                      >
                        {msg.author}
                      </span>
                    )}
                    <div
                      className={clsx(styles.msgBubble, {
                        [styles.msgBubbleOwn]: msg.isOwn
                      })}
                    >
                      <p className={styles.msgText}>{msg.text}</p>
                      <span className={styles.msgTime}>{msg.time}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={chatEndRef} />
            </div>
            <div className={styles.chatInput}>
              <textarea
                className={styles.chatTextarea}
                placeholder="Ваш ответ"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeydown}
                rows={1}
              />
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div className={styles.history}>
            {(kpiData.history ?? []).map((event) => (
              <div key={event.id} className={styles.historyItem}>
                {event.isNew && (
                  <span className={clsx(styles.dot, styles.historyDot)} />
                )}
                <div className={styles.historyText}>
                  <span className={styles.historyAuthor}>{event.author}</span>{' '}
                  {event.action}
                </div>
                <div className={styles.historyMeta}>
                  <span className={styles.historyTime}>{event.time}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>,
    document.body
  );
}

function formatValue(value: number | null): string {
  if (value === null) return '—';
  return value.toLocaleString('ru-RU');
}

interface KpiMiniTableProps {
  kpi: KpiItem;
  isEditing: boolean;
  editingCell: EditingCell | null;
  editValue: string;
  onStartEditing: (
    year: number,
    field: 'fact6months' | 'fact9months' | 'fact',
    currentValue: number | null
  ) => void;
  onEditValueChange: (value: string) => void;
  onSaveEdit: () => void;
}

function KpiMiniTable({
  kpi,
  isEditing,
  editingCell,
  editValue,
  onStartEditing,
  onEditValueChange,
  onSaveEdit
}: KpiMiniTableProps) {
  const years = kpi.years.map((y) => y.year);

  const renderEditableCell = (
    year: number,
    field: 'fact6months' | 'fact9months' | 'fact',
    value: number | null
  ) => {
    const isCellEditing =
      editingCell?.year === year && editingCell?.field === field;

    if (isEditing && isCellEditing) {
      return (
        <Input
          label=""
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          onBlur={onSaveEdit}
          type="number"
          className={styles.editInput}
          variant={'edit'}
        />
      );
    }

    return (
      <span
        className={clsx({
          [styles.editableValue]: isEditing
        })}
        onClick={() => {
          if (isEditing) {
            onStartEditing(year, field, value);
          }
        }}
      >
        {formatValue(value)}
      </span>
    );
  };

  return (
    <div className={styles.miniTable}>
      <div className={styles.desktopTable}>
        <div className={styles.miniTableHead}>
          <div className={styles.miniTableYearCell} />
          <div className={clsx(styles.miniTableCol, styles.miniTableColBorder)}>
            План
          </div>
          <div className={styles.miniTableCol}>Факт, 6 мес</div>
          <div className={styles.miniTableCol}>Факт, 9 мес</div>
          <div
            className={clsx(styles.miniTableCol, styles.miniTableColHighlight)}
          >
            Факт, год
          </div>
        </div>
        {kpi.years.map((y) => (
          <div key={y.year} className={styles.miniTableRow}>
            <div className={styles.miniTableYearCell}>{y.year}</div>
            <div className={clsx(styles.miniTableCol, styles.miniTableColPlan)}>
              {formatValue(y.plan)}
            </div>
            <div className={styles.miniTableCol}>
              {renderEditableCell(y.year, 'fact6months', y.fact6months ?? null)}
            </div>
            <div className={styles.miniTableCol}>
              {renderEditableCell(y.year, 'fact9months', y.fact9months ?? null)}
            </div>
            <div
              className={clsx(
                styles.miniTableCol,
                styles.miniTableColHighlight
              )}
            >
              {renderEditableCell(y.year, 'fact', y.fact)}
            </div>
          </div>
        ))}
      </div>

      <div className={styles.mobileTable}>
        <div className={styles.mobileTableHead}>
          <div className={styles.mobileTableLabelCell} />
          {years.map((year) => (
            <div key={year} className={styles.mobileTableYearCell}>
              {year}
            </div>
          ))}
        </div>
        <div className={clsx(styles.mobileTableRow, styles.mobileTableRowPlan)}>
          <div
            className={clsx(
              styles.mobileTableLabelCell,
              styles.mobileTableLabelPlan
            )}
          >
            План
          </div>
          {kpi.years.map((y) => (
            <div key={y.year} className={styles.mobileTableCell}>
              {formatValue(y.plan)}
            </div>
          ))}
        </div>
        <div className={clsx(styles.mobileTableRow)}>
          <div className={styles.mobileTableLabelCell}>Факт, 6 мес</div>
          {kpi.years.map((y) => (
            <div key={y.year} className={styles.mobileTableCell}>
              {renderEditableCell(y.year, 'fact6months', y.fact6months ?? null)}
            </div>
          ))}
        </div>
        <div className={clsx(styles.mobileTableRow)}>
          <div className={styles.mobileTableLabelCell}>Факт, 9 мес</div>
          {kpi.years.map((y) => (
            <div key={y.year} className={styles.mobileTableCell}>
              {renderEditableCell(y.year, 'fact9months', y.fact9months ?? null)}
            </div>
          ))}
        </div>
        <div className={clsx(styles.mobileTableRow, styles.mobileTableRowFact)}>
          <div
            className={clsx(
              styles.mobileTableLabelCell,
              styles.mobileTableLabelFact
            )}
          >
            Факт, год
          </div>
          {kpi.years.map((y) => (
            <div key={y.year} className={styles.mobileTableCell}>
              {renderEditableCell(y.year, 'fact', y.fact)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
